import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface ServicesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Utility function to check if modal should be shown
export const shouldShowModal = (): boolean => {
  if (typeof window === "undefined") return false;
  return !sessionStorage.getItem("servicesModalShown");
};

// Utility function to mark modal as shown
export const markModalAsShown = (): void => {
  if (typeof window !== "undefined") {
    sessionStorage.setItem("servicesModalShown", "true");
  }
};

const ServicesModal: React.FC<ServicesModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.style.overflow = "hidden";
    } else {
      setIsVisible(false);
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleClose = () => {
    markModalAsShown();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${
          isVisible ? "opacity-50" : "opacity-0"
        }`}
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className={`relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 transform transition-all duration-300 ${
          isVisible ? "scale-100 opacity-100" : "scale-95 opacity-0"
        }`}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Content */}
        <div className="p-8">
          {/* Header with icon */}
          <div className="text-center mb-6">
            <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="h-8 w-8 text-primary-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t("servicesModal.title")}
            </h2>
            <p className="text-gray-600">{t("servicesModal.subtitle")}</p>
          </div>

          {/* Benefits */}
          <div className="space-y-3 mb-6">
            {[
              t("servicesModal.benefit1"),
              t("servicesModal.benefit2"),
              t("servicesModal.benefit3"),
            ].map((benefit, index) => (
              <div key={index} className="flex items-start">
                <svg
                  className="h-5 w-5 text-primary-500 mr-3 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className="text-sm text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>

          {/* Call to Actions */}
          <div className="space-y-3">
            <Link
              to="/contact"
              onClick={handleClose}
              className="w-full bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg px-6 py-3 text-center transition-all duration-300 transform hover:scale-105 inline-block"
            >
              {t("servicesModal.getQuote")}
            </Link>

            <a
              href="tel:514-979-8020"
              className="w-full border border-primary-500 text-primary-500 hover:bg-primary-50 font-semibold rounded-lg px-6 py-3 text-center transition-all duration-300 inline-flex items-center justify-center"
            >
              <svg
                className="h-5 w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              {t("servicesModal.callNow")}
            </a>
          </div>

          {/* Small disclaimer */}
          <p className="text-xs text-gray-500 text-center mt-4">
            {t("servicesModal.disclaimer")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ServicesModal;
